import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import {
  type CreatePriceTableDto,
  type IEquipmentPayment,
  type IPriceRange,
  type IPriceTableConditionGroup,
  type IServicePayment,
  type ISimcardPayment,
  PriceTableStatus,
  priceTableApi,
} from '@/api/commercial/price-tables';
import { useToast } from '@/hooks/use-toast';
import { centsToPtbr, ptbrToCents } from '@/lib/formatters';
import {
  type BrazilianUF,
  type CreatePriceTableFormData,
  priceTableSchema,
} from '@/schemas/commercial/price-table.schema';
import { validatePriceTableConditions } from '@/utils/validate-price-table-conditions';

interface UsePriceTableFormProps {
  priceTableId?: string;
  editMode?: boolean;
  cloneMode?: boolean;
}

type Status = 'red' | 'yellow' | 'green' | 'blue';

const STATUS_STYLES: Record<Status, string> = {
  red: 'bg-red-100 border-l-red-500 text-red-800',
  yellow: 'bg-yellow-100 border-l-yellow-500 text-yellow-800',
  green: 'bg-green-100 border-l-green-500 text-green-800',
  blue: 'bg-blue-100 border-l-blue-500 text-blue-800',
};

const uid = () => crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);

const createEmptyCondition = () => ({
  id: uid(),
  salesFor: [] as BrazilianUF[],
  billingLimitInCents: '',
  toBillFor: '',
});

const createEmptyGroup = () => ({
  id: uid(),
  conditions: [createEmptyCondition()],
  priority: false,
});

const ensureFutureStartDate = (startDateTime: Date) => {
  const now = new Date();
  const oneMinuteFromNow = new Date(now.getTime() + 60000);

  if (startDateTime <= now) {
    return oneMinuteFromNow;
  }

  return startDateTime;
};

const createPriceRange = (tiers: any[]): IPriceRange[] => {
  if (!tiers || !Array.isArray(tiers)) {
    return [];
  }

  const filtered = tiers.filter((tier) => {
    const hasFrom = tier?.from !== undefined && tier?.from !== null && tier?.from !== '';
    const hasPrice =
      (tier?.unitPriceInCents !== undefined && tier?.unitPriceInCents !== null) ||
      (tier?.unitPrice !== undefined && tier?.unitPrice !== null && tier?.unitPrice !== '') ||
      (tier?.pricePerUnit !== undefined &&
        tier?.pricePerUnit !== null &&
        tier?.pricePerUnit !== '');

    return hasFrom && hasPrice;
  });

  return filtered.map((tier, index) => {
    const from = typeof tier.from === 'string' ? Number(tier.from) : Number(tier.from);
    const isLast = index === filtered.length - 1;

    // Last tier has open-ended range: store as null
    let to: number | null;
    if (tier?.to === undefined || tier?.to === null || tier?.to === '') {
      to = isLast ? null : 0;
    } else {
      const toNum = typeof tier.to === 'string' ? Number(tier.to) : Number(tier.to);
      // Migrate legacy MAX_SAFE_INTEGER to null
      to = toNum === Number.MAX_SAFE_INTEGER ? null : toNum;
    }

    // Support both InCents (from API) and string/number (from form)
    if (tier?.unitPriceInCents !== undefined && tier?.unitPriceInCents !== null) {
      return { from, to, unitPriceInCents: Number(tier.unitPriceInCents) };
    }

    const unitPriceSource =
      tier?.unitPrice !== undefined && tier?.unitPrice !== null
        ? tier.unitPrice
        : tier?.pricePerUnit;

    const unitPriceInCents = ptbrToCents(
      typeof unitPriceSource === 'string'
        ? unitPriceSource
        : Number(unitPriceSource).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
    );

    return { from, to, unitPriceInCents };
  });
};

export function usePriceTableForm({
  priceTableId,
  editMode = false,
  cloneMode = false,
}: UsePriceTableFormProps = {}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const [existingEquipmentPayment, setExistingEquipmentPayment] = useState<IEquipmentPayment[]>([]);
  const [existingEquipmentSimcardPayment, setExistingEquipmentSimcardPayment] = useState<
    IEquipmentPayment[]
  >([]);
  const [existingSimcardPayment, setExistingSimcardPayment] = useState<ISimcardPayment[]>([]);
  const [existingServicePayment, setExistingServicePayment] = useState<IServicePayment[]>([]);

  const [priceTableStatus, setPriceTableStatus] = useState<PriceTableStatus | null>(null);
  const [priceTableName, setPriceTableName] = useState<string>('');
  const [priceTableIdentifier, setPriceTableIdentifier] = useState<string | null>(null);

  const [messageErrorCondition, setMessageErrorCondition] = useState<{
    status: Status;
    message: string[];
  }>({ status: 'red', message: [] });

  // Fetch existing price table data
  const { data: existingPriceTable, isLoading: loadingPriceTable } = useQuery({
    queryKey: ['price-table', priceTableId],
    // biome-ignore lint/style/noNonNullAssertion: guarded by enabled
    queryFn: () => priceTableApi.findOne(priceTableId!),
    enabled: !!(editMode || cloneMode) && !!priceTableId,
  });

  const form = useForm<CreatePriceTableFormData>({
    resolver: zodResolver(priceTableSchema) as Resolver<CreatePriceTableFormData>,
    defaultValues: {
      name: '',
      startDateTime: undefined,
      endDateTime: undefined,
      isTemporary: false,
      groups: [createEmptyGroup()],
      billingConfig: {
        salesFor: '',
        billingLimitInCents: '',
        billTo: '',
      },
      equipmentWithSim: {},
      equipmentWithoutSim: {},
      simCards: [],
      accessories: {},
      services: [],
    },
  });

  const processExistingPaymentData = useCallback((equipmentPayment: IEquipmentPayment[]) => {
    const equipmentWithSim: Record<string, any> = {};
    const equipmentWithoutSim: Record<string, any> = {};
    const accessories: Record<string, any> = {};

    equipmentPayment.forEach((payment) => {
      const isAccessory = payment.productId?.startsWith('ACC') || false;
      const targetCategory = isAccessory ? accessories : equipmentWithSim;

      if (!targetCategory[payment.productId]) {
        targetCategory[payment.productId] = {};
      }

      const paymentKey = payment.paymentType === 'credit' ? 'onDemand' : 'onSight';

      if (isAccessory) {
        const priceKey = payment.paymentType === 'credit' ? 'singlePrice' : 'cashPrice';
        const rangeKey =
          payment.paymentType === 'credit' ? 'useQuantityRange' : 'useCashQuantityRange';
        const tiersKey = payment.paymentType === 'credit' ? 'priceTiers' : 'cashPriceTiers';

        if (payment.type === 'batch' && payment.priceRange.length > 0) {
          targetCategory[payment.productId][rangeKey] = true;
          targetCategory[payment.productId][tiersKey] = payment.priceRange.map((range) => ({
            from: range.from,
            to: range.to,
            unitPriceInCents: range.unitPriceInCents,
          }));
        } else {
          targetCategory[payment.productId][rangeKey] = false;
          targetCategory[payment.productId][priceKey] = centsToPtbr(payment.unitPriceInCents);
        }
      } else {
        targetCategory[payment.productId][paymentKey] = {
          ...payment,
          unitPriceInCents: payment.unitPriceInCents,
          priceRange: payment.priceRange.map((range) => ({
            ...range,
            unitPriceInCents: range.unitPriceInCents,
          })),
        };
      }
    });

    return { equipmentWithSim, equipmentWithoutSim, accessories };
  }, []);

  // Load existing price table data into form
  useEffect(() => {
    if (!existingPriceTable) return;

    setPriceTableStatus(cloneMode ? PriceTableStatus.DRAFT : (existingPriceTable.status ?? null));

    const formattedSeq = existingPriceTable.seq
      ? `PT-${existingPriceTable.seq}`
      : (existingPriceTable.id ?? null);
    setPriceTableIdentifier(cloneMode ? null : formattedSeq);

    const tableName = cloneMode
      ? `${existingPriceTable.name} (Cópia)`
      : (existingPriceTable.name ?? '');
    setPriceTableName(tableName);

    setExistingEquipmentPayment(existingPriceTable.equipmentPayment ?? []);
    setExistingEquipmentSimcardPayment(existingPriceTable.equipmentSimcardPayment ?? []);
    setExistingSimcardPayment(existingPriceTable.simcardPayment ?? []);
    setExistingServicePayment(existingPriceTable.servicePayment ?? []);

    const { equipmentWithoutSim, accessories } = processExistingPaymentData(
      existingPriceTable.equipmentPayment ?? [],
    );
    const { equipmentWithSim } = processExistingPaymentData(
      existingPriceTable.equipmentSimcardPayment ?? [],
    );

    let startDateTime: Date | undefined;
    let endDateTime: Date | undefined;

    if (cloneMode) {
      startDateTime = undefined;
      endDateTime = undefined;
    } else {
      startDateTime = existingPriceTable.startDateTime
        ? new Date(existingPriceTable.startDateTime)
        : undefined;

      if (existingPriceTable.isTemporary) {
        endDateTime = existingPriceTable.endDateTime
          ? new Date(existingPriceTable.endDateTime)
          : undefined;
      }
    }

    form.reset({
      name: tableName,
      startDateTime,
      endDateTime,
      isTemporary: existingPriceTable.isTemporary ?? false,
      billingConfig: { salesFor: '', billingLimitInCents: '', billTo: '' },
      equipmentWithSim,
      groups: (existingPriceTable.groups ?? []).map((g) => ({
        ...g,
        conditions: (g.conditions ?? []).map((c) => ({
          ...c,
          billingLimitInCents: centsToPtbr(c.billingLimitInCents),
        })),
      })),
      equipmentWithoutSim,
      simCards: (existingPriceTable.simcardPayment ?? []).map((sc) => ({
        ...sc,
        priceWithoutDeviceInCents: sc.priceWithoutDeviceInCents,
        priceInBundleInCents: sc.priceInBundleInCents,
      })),
      accessories,
      services: (existingPriceTable.servicePayment ?? []).map((svc) => ({
        ...svc,
        monthlyPriceInCents: svc.monthlyPriceInCents,
        yearlyPriceInCents: svc.yearlyPriceInCents,
        fixedPriceInCents: svc.fixedPriceInCents,
      })),
    });
  }, [existingPriceTable, cloneMode, form, processExistingPaymentData]);

  // Watch for name changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'name' && value.name !== undefined) {
        setPriceTableName(value.name);
        form.clearErrors('name');
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Watch for isTemporary changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'isTemporary' && value.isTemporary !== undefined) {
        if (!value.isTemporary) {
          form.setValue('endDateTime', undefined);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const createEquipmentPayment = (
    equipmentModel: string,
    prices: any,
    paymentType: 'credit' | 'upfront',
    type: 'batch' | 'unit',
  ) => {
    const priceRange =
      type === 'batch'
        ? createPriceRange(
            paymentType === 'upfront' ? prices.cashPriceTiers || [] : prices.priceTiers || [],
          )
        : [];

    return {
      type,
      paymentType,
      productId: equipmentModel,
      productName: equipmentModel,
      unitPriceInCents:
        type === 'unit' ? ptbrToCents(prices.singlePrice || prices.cashPrice || '0') : 0,
      priceRange,
    };
  };

  const handleEquipmentPriceChange = (
    equipmentModel: string,
    prices: any,
    type: 'withSim' | 'withoutSim',
  ) => {
    const currentData = form.getValues();
    const equipmentPayment: {
      onSight?: IEquipmentPayment;
      onDemand?: IEquipmentPayment;
    } = {};

    if (prices.useQuantityRange && prices.priceTiers?.length > 0) {
      equipmentPayment.onDemand = createEquipmentPayment(equipmentModel, prices, 'credit', 'batch');
    } else if (prices.singlePrice) {
      equipmentPayment.onDemand = createEquipmentPayment(equipmentModel, prices, 'credit', 'unit');
    }

    if (prices.useCashQuantityRange && prices.cashPriceTiers?.length > 0) {
      equipmentPayment.onSight = createEquipmentPayment(equipmentModel, prices, 'upfront', 'batch');
    } else if (prices.cashPrice) {
      equipmentPayment.onSight = createEquipmentPayment(equipmentModel, prices, 'upfront', 'unit');
    }

    const targetKey = type === 'withSim' ? 'equipmentWithSim' : 'equipmentWithoutSim';
    form.setValue(targetKey, {
      ...currentData[targetKey],
      [equipmentModel]: equipmentPayment,
    });
  };

  const handleSimCardPriceChange = (prices: any) => {
    const simcardPayments: ISimcardPayment[] =
      prices.simCardTiers
        ?.filter(
          (tier: any) => tier.carriers?.length > 0 && tier.dataMB && tier.type && tier.supplier,
        )
        .map((tier: any) => ({
          carriers: tier.carriers,
          dataAmountMb: Number(tier.dataMB.replace('MB', '')),
          planType: tier.type,
          provider: tier.supplier,
          priceWithoutDeviceInCents: ptbrToCents(tier.priceWithoutEquipment),
          priceInBundleInCents: ptbrToCents(tier.priceInCombo),
        })) || [];

    form.setValue('simCards', simcardPayments);
  };

  const handleAccessoryPriceChange = (accessory: string, prices: any) => {
    const currentData = form.getValues();
    form.setValue('accessories', {
      ...currentData.accessories,
      [accessory]: prices,
    });
  };

  const handleServicePriceChange = (services: any[]) => {
    const transformedServices: IServicePayment[] = services
      .filter((service) => service.service)
      .map((service) => ({
        serviceId: service.service || '',
        monthlyPriceInCents: service.monthlyPrice ? ptbrToCents(service.monthlyPrice) : undefined,
        yearlyPriceInCents: service.annualPrice ? ptbrToCents(service.annualPrice) : undefined,
        fixedPriceInCents: service.fixedPrice ? ptbrToCents(service.fixedPrice) : undefined,
      }));

    form.setValue('services', transformedServices);
  };

  const processEquipmentData = (
    equipment: Record<string, any>,
    equipmentPayment: IEquipmentPayment[],
  ) => {
    Object.entries(equipment).forEach(([_productId, payment]) => {
      if (payment?.onSight) {
        const onSightPayment = {
          ...payment.onSight,
          paymentType: 'upfront' as const,
        };
        equipmentPayment.push(onSightPayment);
      }
      if (payment?.onDemand) {
        const onDemandPayment = {
          ...payment.onDemand,
          paymentType: 'credit' as const,
        };
        equipmentPayment.push(onDemandPayment);
      }
    });
  };

  const processAccessoryData = (
    accessories: Record<string, any>,
    equipmentPayment: IEquipmentPayment[],
  ) => {
    Object.entries(accessories).forEach(([accessoryId, accessoryData]) => {
      const accessoryPayment: any = accessoryData;

      const addPayment = (
        paymentType: 'credit' | 'upfront',
        useRange: string,
        tiers: string,
        singlePrice: string,
      ) => {
        if (accessoryPayment?.[useRange] && accessoryPayment?.[tiers]?.length > 0) {
          equipmentPayment.push({
            type: 'batch',
            paymentType,
            productId: accessoryId,
            productName: accessoryId,
            unitPriceInCents: 0,
            priceRange: createPriceRange(accessoryPayment[tiers]),
          });
        } else if (accessoryPayment?.[singlePrice]) {
          equipmentPayment.push({
            type: 'unit',
            paymentType,
            productId: accessoryId,
            productName: accessoryId,
            unitPriceInCents: ptbrToCents(accessoryPayment[singlePrice]),
            priceRange: [],
          });
        }
      };

      addPayment('credit', 'useQuantityRange', 'priceTiers', 'singlePrice');
      addPayment('upfront', 'useCashQuantityRange', 'cashPriceTiers', 'cashPrice');
    });
  };

  const transformToPriceTablePayload = (data: CreatePriceTableFormData): CreatePriceTableDto => {
    const equipmentPayment: IEquipmentPayment[] = [];
    const equipmentSimcardPayment: IEquipmentPayment[] = [];

    processEquipmentData(data.equipmentWithoutSim || {}, equipmentPayment);
    processEquipmentData(data.equipmentWithSim || {}, equipmentSimcardPayment);
    processAccessoryData(data.accessories || {}, equipmentPayment);

    const groups: IPriceTableConditionGroup[] = (data.groups || []).map((group) => ({
      ...group,
      conditions: (group.conditions || []).map((condition) => ({
        ...condition,
        salesFor: (condition.salesFor ?? []) as BrazilianUF[],
        billingLimitInCents: ptbrToCents(condition.billingLimitInCents),
        toBillFor: condition.toBillFor ?? '',
      })),
    }));

    return {
      name: data.name,
      startDateTime: data.startDateTime,
      endDateTime: data.isTemporary ? data.endDateTime : undefined,
      isTemporary: data.isTemporary,
      equipmentPayment,
      equipmentSimcardPayment,
      simcardPayment: data.simCards || [],
      servicePayment: data.services || [],
      groups,
    };
  };

  const getOperationMessages = (status: PriceTableStatus) => {
    const isUpdate = editMode && !cloneMode;
    const isDraft = status === 'DRAFT';

    if (isUpdate) {
      return {
        message: isDraft ? 'Alterações salvas!' : 'Tabela de preços atualizada com sucesso!',
        description: isDraft
          ? 'Suas alterações foram salvas como rascunho.'
          : 'Tabela de preços atualizada com sucesso!',
      };
    }

    if (cloneMode) {
      return {
        message: 'Tabela clonada com sucesso!',
        description: 'Tabela foi clonada e você foi redirecionado para editá-la.',
      };
    }

    return {
      message: isDraft ? 'Rascunho salvo!' : 'Tabela de preços criada com sucesso!',
      description: isDraft
        ? 'Suas alterações foram salvas como rascunho.'
        : 'Tabela de preços criada com sucesso!',
    };
  };

  const handleFormSubmission = async (
    data: CreatePriceTableFormData,
    status: PriceTableStatus = PriceTableStatus.DRAFT,
  ) => {
    try {
      const payload = transformToPriceTablePayload(data);
      const isUpdate = editMode && !cloneMode;

      // biome-ignore lint/style/noNonNullAssertion: priceTableId is guaranteed to exist when isUpdate is true
      const result = isUpdate
        ? await priceTableApi.update(priceTableId!, payload)
        : await priceTableApi.create(payload);

      if (result?.success) {
        setPriceTableStatus(status);

        if (payload.equipmentPayment) {
          setExistingEquipmentPayment(payload.equipmentPayment);
        }
        if (payload.equipmentSimcardPayment) {
          setExistingEquipmentSimcardPayment(payload.equipmentSimcardPayment);
        }
        if (payload.simcardPayment) {
          setExistingSimcardPayment(payload.simcardPayment);
        }
        if (payload.servicePayment) {
          setExistingServicePayment(payload.servicePayment);
        }

        // Invalidate price tables list cache so it updates without manual refresh
        await queryClient.invalidateQueries({ queryKey: ['price-tables'] });
        await queryClient.invalidateQueries({ queryKey: ['price-table-history'] });

        const { message, description } = getOperationMessages(status);
        toast({ title: message, description });

        const isCreatingNew = !editMode || cloneMode;
        if (isCreatingNew && result.id) {
          navigate(`/commercial/price-table/form/edit/${result.id}`);
        }
      } else {
        const errorMsg = result?.error?.global || 'Falha ao processar a tabela de preços!';
        toast({
          title: 'Erro de Validação!',
          description: errorMsg,
          variant: 'destructive',
        });

        ['name', 'startDateTime', 'endDateTime'].forEach((field) => {
          if (result?.error?.fields && field in result.error.fields) {
            form.setError(field as keyof CreatePriceTableFormData, {
              message: result.error.fields[field],
            });
          }
        });
      }
    } catch (_error) {
      toast({
        title: 'Erro!',
        description: 'Falha inesperada ao processar a tabela de preços!',
        variant: 'destructive',
      });
    }
  };

  const getValidationErrorMessage = (errors: any) => {
    if (errors.name || errors.startDateTime || errors.endDateTime) {
      return 'Erro ao tentar salvar! Antes de salvar a tabela é necessário preencher os campos obrigatórios.';
    }

    if (errors.groups) {
      return 'Preencha todos os campos obrigatórios nas condições de faturamento.';
    }

    return 'Preencha todos os campos obrigatórios.';
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);

    try {
      const isValid = await form.trigger();
      if (!isValid) {
        const errors = form.formState.errors;
        const errorMessage = getValidationErrorMessage(errors);

        toast({
          title: 'Erro ao tentar salvar!',
          description: errorMessage,
          variant: 'destructive',
        });
        return;
      }

      const validationResult = validatePriceTableConditions(form.getValues().groups || []);

      setMessageErrorCondition({
        status: validationResult.status,
        message: validationResult.messages ?? [],
      });

      if (validationResult.status !== 'green') {
        toast({
          title: 'Erro de Validação!',
          description:
            validationResult.messages[0] ??
            'Condições de faturamento inválidas, ajuste antes de salvar.',
          variant: 'destructive',
        });
        return;
      }

      const currentData = form.getValues();
      const adjustedData = {
        ...currentData,
        startDateTime: ensureFutureStartDate(currentData.startDateTime),
      };

      await handleFormSubmission(adjustedData, PriceTableStatus.DRAFT);
    } finally {
      setLoading(false);
    }
  };

  const handleValidationConditions = async () => {
    try {
      const groups = (form.getValues().groups || []).map((group: any) => ({
        ...group,
        conditions: (group.conditions || []).map((condition: any) => ({
          ...condition,
          salesFor: condition.salesFor ?? [],
        })),
      }));
      const result = validatePriceTableConditions(groups);
      setMessageErrorCondition({
        status: result.status,
        message: result.messages ?? [],
      });
    } catch (_error) {
      toast({
        title: 'Erro!',
        description: 'Falha ao validar as condições!',
        variant: 'destructive',
      });
    }
  };

  const status = (messageErrorCondition.status ?? 'red') as Status;

  return {
    form,
    handleSubmit,
    handleCancel: () => navigate('/commercial/price-table'),
    loading,
    loadingPriceTable,
    validateForm: () => form.trigger(),
    getFormErrors: () => form.formState.errors,
    isFormDirty: () => form.formState.isDirty,
    handleEquipmentPriceChange,
    handleSimCardPriceChange,
    handleAccessoryPriceChange,
    handleServicePriceChange,
    handleValidationConditions,
    messageErrorCondition,
    STATUS_STYLES,
    status,
    existingEquipmentPayment,
    existingEquipmentSimcardPayment,
    existingSimcardPayment,
    existingServicePayment,
    priceTableStatus,
    priceTableName,
    priceTableIdentifier,
  };
}
